import { HTTPError } from 'ky';

type JsonApiError = {
  errors?: Array<{ status?: string; title?: string; detail?: string }>;
};

/**
 * Shared error handler for PO tool operations.
 * Parses Armageddon JSON:API errors into agent-friendly messages.
 */
export async function handlePoError(error: unknown): Promise<{ error: string }> {
  if (!(error instanceof HTTPError)) {
    throw error;
  }

  const { status } = error.response;

  if (status === 401) {
    return { error: 'Session expired. Please log in again.' };
  }

  if (status === 403) {
    return { error: 'Permission denied. You do not have access to this purchase order.' };
  }

  if (status === 404) {
    return { error: 'Purchase order not found.' };
  }

  // 409 and 422 may include JSON:API error details
  if (status === 409 || status === 422) {
    try {
      const body = (await error.response.json()) as JsonApiError;
      const detail = body.errors?.[0]?.detail ?? body.errors?.[0]?.title;
      if (detail) {
        return { error: detail };
      }
    } catch {
      // Could not parse body — fall through
    }
    return {
      error:
        status === 409
          ? 'Conflict — the purchase order may have unresolved changes or be in a terminal status.'
          : 'Validation error — check the purchase order fields and try again.',
    };
  }

  throw error;
}
